// =============================================================
// FLOW (c): Work board → work order drawer.
//
// Lists the REAL tasks from /api/v1 with status filters + relative-overdue
// signal (core rule). Opening a task shows the WO drawer; "Complete work order"
// calls POST /tasks/:id/complete, where the SERVER enforces the LOTO gate (it
// returns 422 loto_blocked when the gate isn't satisfied — surfaced as a toast).
//
// WAVE 2 (design-parity depth):
//   - sortable column headers (client-side, toggle asc/desc) over the loaded
//     list — by work order, status, due date, device, priority;
//   - row multi-select checkboxes + a header select-all;
//   - a bulk action bar (appears when ≥1 row is selected): "Reassign…" drives
//     POST /tasks/mass-replace-performer with a dry-run preview/confirm, and
//     "Reschedule…" drives POST /tasks/mass-reschedule over the SELECTED rows
//     (explicit taskIds) with a date picker → dry-run preview → confirm → write.
//
// HONEST API MAPPING NOTE — /tasks/mass-replace-performer reassigns by
// performer (fromUserId → toUserId, optionally scoped by location/category); it
// does NOT accept an arbitrary task-id list. So bulk "Reassign…" derives the
// single common current performer of the selected rows and reassigns THAT
// performer's upcoming tasks. The dry-run preview shows the true `affected`
// count from the server and the confirm copy states this plainly — selecting
// rows with different performers (or none) disables reassign rather than
// pretending a per-row reassignment the backend can't do. Bulk "Reschedule…"
// has no such limitation: /tasks/mass-reschedule accepts the explicit taskIds
// selector, so it operates directly on the checked rows (completed/cancelled
// rows are skipped server-side; the dry-run preview reports the true count).
// =============================================================

import { useMemo, useState } from "react";
import { relativeOverdue, type StatusKind, type Task, type TaskStatus } from "@perpetuum/core";
import { useActionableTasks, useCollection, useDevices, useMassReplace, useMassReschedule, useTasksByStatus } from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useSync } from "../app/SyncContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS, OverdueBar, StatusChip, SyncBadge } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";
import { WoDetail } from "./WoDetail.js";

type Filter = "all" | "open" | "in_progress" | "completed";
type SortKey = "wo" | "status" | "due" | "device" | "priority";
type SortDir = "asc" | "desc";

// Status → [chip kind, i18n key]. Labels are resolved through t() at render
// time (the same canonical `status.*` keys the tasks register reuses), so the
// chips localize instead of showing hardcoded English.
const STATUS_CHIP: Record<TaskStatus, [StatusKind, string]> = {
  open: ["info", "status.open"],
  in_progress: ["warning", "status.in_progress"],
  paused: ["paused", "status.paused"],
  completed: ["success", "status.completed"],
  cancelled: ["paused", "status.cancelled"],
  postponed: ["paused", "status.postponed"],
};

const PRIO_CHIP: Record<string, string> = {
  critical: "critical",
  high: "warning",
  normal: "paused",
  low: "paused",
};

// Sort rank for status (lifecycle order) and priority (urgency order). Lower =
// earlier in ascending order.
const STATUS_RANK: Record<TaskStatus, number> = {
  open: 0,
  in_progress: 1,
  paused: 2,
  postponed: 3,
  completed: 4,
  cancelled: 5,
};
const PRIO_RANK: Record<string, number> = { critical: 0, high: 1, normal: 2, low: 3 };

/** Stable comparison helper: numbers sort numerically, strings localeCompare. */
function cmp(a: number | string, b: number | string): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

export function WorkBoard() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast, confirm } = useChrome();
  const mass = useMassReplace();
  const reschedule = useMassReschedule();
  const usersQ = useCollection("users");
  const devicesQ = useDevices();
  const { stateFor } = useSync();
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Task | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("due");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [reassignOpen, setReassignOpen] = useState(false);
  const [toUserId, setToUserId] = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [avoidWeekend, setAvoidWeekend] = useState(false);

  // The board loads the ACTIONABLE set (non-completed) by default with a
  // generous limit, so Open / In progress / All are correct on live data (the
  // generic bounded window is dominated by the completed historical backlog and
  // would show 0 actionable). The "Completed" tab loads the completed set on
  // demand (its own server-side `?status=completed` page) — only fetched once
  // that tab is actually viewed.
  const actionableQ = useActionableTasks();
  const completedQ = useTasksByStatus(["completed"], { enabled: filter === "completed" });

  const actionable = actionableQ.data ?? [];
  const completed = completedQ.data ?? [];

  // The visible list depends on the active tab: the Completed tab shows the
  // separately-loaded completed set; every other tab shows the actionable set.
  const tasks = filter === "completed" ? completed : actionable;
  // The query backing the visible list (drives the loading skeleton + empty).
  const activeQ = filter === "completed" ? completedQ : actionableQ;

  // Tab counts. open / in_progress / all are exact over the loaded actionable
  // set (it carries every non-completed task ≤ the generous limit). The
  // completed count comes from its own load and is only known once that tab has
  // been viewed — until then we show "—" rather than a wrong number, since the
  // completed backlog is never loaded up-front.
  const counts = useMemo(
    () => ({
      all: actionable.length,
      open: actionable.filter((x) => x.status === "open").length,
      in_progress: actionable.filter((x) => x.status === "in_progress").length,
      completed: completedQ.isSuccess ? completed.filter((x) => x.isCompleted || x.status === "completed").length : null,
    }),
    [actionable, completed, completedQ.isSuccess],
  );

  const filtered = useMemo(
    () =>
      tasks.filter((x) =>
        filter === "all"
          ? true
          : filter === "completed"
            ? x.isCompleted || x.status === "completed"
            : x.status === filter,
      ),
    [tasks, filter],
  );

  // Client-side sort over the loaded list (the server returns up to the limit).
  const rows = useMemo(() => {
    const keyOf = (task: Task): number | string => {
      switch (sortKey) {
        case "wo":
          return (task.number ?? task.id).toLowerCase();
        case "status":
          return STATUS_RANK[task.status] ?? 99;
        case "device":
          return (task.deviceId ?? "").toLowerCase();
        case "priority":
          return PRIO_RANK[task.priority ?? "normal"] ?? 2;
        case "due":
          // Sort by dueDate when present, else by days-late (overdue), else last.
          return task.dueDate ? Date.parse(task.dueDate) : task.dueDays != null ? Date.now() + task.dueDays * 86_400_000 : Number.MAX_SAFE_INTEGER;
      }
    };
    const sorted = [...filtered].sort((a, b) => cmp(keyOf(a), keyOf(b)));
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [filtered, sortKey, sortDir]);

  // Keep the checked set in sync with the visible rows (drop ids filtered out).
  const visibleIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const checkedVisible = useMemo(() => [...checked].filter((id) => visibleIds.has(id)), [checked, visibleIds]);
  const allChecked = rows.length > 0 && checkedVisible.length === rows.length;
  const someChecked = checkedVisible.length > 0 && !allChecked;

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const ariaSort = (key: SortKey): "ascending" | "descending" | "none" =>
    key === sortKey ? (sortDir === "asc" ? "ascending" : "descending") : "none";

  const toggleRow = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (allChecked) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  const clearSelection = () => setChecked(new Set());

  // The selected rows' distinct current performers. mass-replace works by
  // performer, so we can only reassign when the selection shares ONE performer.
  const selectedTasks = rows.filter((r) => checked.has(r.id));
  const distinctPerformers = useMemo(() => {
    const ids = new Set<string>();
    for (const tk of selectedTasks) for (const uid of tk.assignedUserIds ?? []) ids.add(uid);
    return [...ids];
  }, [selectedTasks]);
  const commonPerformer = distinctPerformers.length === 1 ? distinctPerformers[0]! : null;

  const users = usersQ.data ?? [];
  // Resolve a user id → display name. A deleted/unknown id must never surface as
  // a raw UUID — fall back to a localized "(unknown)" instead.
  const userName = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u?.name != null ? String(u.name) : t("common.unknownUser");
  };
  const devices = devicesQ.data ?? [];
  const deviceName = (id: string): string | undefined => devices.find((d) => d.id === id)?.name;

  const onReassign = async () => {
    if (!commonPerformer || !toUserId) return;
    try {
      // Dry-run first → the SERVER's true affected count (we never invent it).
      const preview = await mass.mutateAsync({ fromUserId: commonPerformer, toUserId, dryRun: true });
      const ok = await confirm({
        title: t("workboard.bulk.reassignConfirmTitle"),
        body: t("workboard.bulk.reassignConfirm", {
          n: preview.affected,
          from: userName(commonPerformer),
          to: userName(toUserId),
        }),
        confirmLabel: t("workboard.bulk.reassign"),
      });
      if (!ok) return;
      const res = await mass.mutateAsync({ fromUserId: commonPerformer, toUserId });
      toast(t("workboard.bulk.reassigned", { n: res.affected }), "success");
      setReassignOpen(false);
      setToUserId("");
      clearSelection();
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };

  const onReschedule = async () => {
    if (!dueDate || checkedVisible.length === 0) return;
    // The picker yields a local YYYY-MM-DD; send midnight UTC so the server's
    // weekend test + stored due date are unambiguous.
    const dueIso = new Date(`${dueDate}T00:00:00.000Z`).toISOString();
    try {
      // Dry-run first → the SERVER's true affected count over the selected rows
      // (completed/cancelled are skipped server-side; we never invent the count).
      const preview = await reschedule.mutateAsync({
        dueDate: dueIso,
        taskIds: checkedVisible,
        avoidWeekend,
        dryRun: true,
      });
      if (preview.affected === 0) {
        toast(t("workboard.bulk.rescheduleNone"), "error");
        return;
      }
      const ok = await confirm({
        title: t("workboard.bulk.rescheduleConfirmTitle"),
        body: t("workboard.bulk.rescheduleConfirm", {
          n: preview.affected,
          date: new Date(preview.dueDate).toLocaleDateString(),
        }),
        confirmLabel: t("workboard.bulk.reschedule"),
      });
      if (!ok) return;
      const res = await reschedule.mutateAsync({ dueDate: dueIso, taskIds: checkedVisible, avoidWeekend });
      toast(t("workboard.bulk.rescheduled", { n: res.affected }), "success");
      setRescheduleOpen(false);
      setDueDate("");
      setAvoidWeekend(false);
      clearSelection();
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };

  const FILTERS: [Filter, string][] = [
    ["all", "workboard.filter.all"],
    ["open", "workboard.filter.open"],
    ["in_progress", "workboard.filter.in_progress"],
    ["completed", "workboard.filter.completed"],
  ];

  const SORT_COLS: { key: SortKey; labelKey: string }[] = [
    { key: "wo", labelKey: "workboard.col.wo" },
    { key: "status", labelKey: "workboard.col.status" },
    { key: "due", labelKey: "workboard.col.due" },
  ];

  return (
    <div>
      <h2 className="d-h2">{t("workboard.title")}</h2>
      <p className="d-sub">{t("workboard.sub")}</p>

      <div className="wb-filters">
        {FILTERS.map(([k, labelKey]) => (
          <button
            key={k}
            className={`wb-fchip ${filter === k ? "on" : ""}`}
            onClick={() => setFilter(k)}
          >
            {t(labelKey)}
            {/* completed count is unknown until that tab loads its own set → "—" */}
            <span className="fc-n">{counts[k] === null ? "—" : counts[k]}</span>
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {/* Sort affordance for keys without a dedicated header cell. */}
        <div className="view-seg" aria-label={t("workboard.sortBy")}>
          <button
            aria-pressed={sortKey === "device"}
            onClick={() => toggleSort("device")}
            title={t("workboard.col.device")}
          >
            {t("workboard.col.device")}
            {sortKey === "device" && <SortCaret dir={sortDir} />}
          </button>
          <button
            aria-pressed={sortKey === "priority"}
            onClick={() => toggleSort("priority")}
            title={t("workboard.col.priority")}
          >
            {t("workboard.col.priority")}
            {sortKey === "priority" && <SortCaret dir={sortDir} />}
          </button>
        </div>
      </div>

      {checkedVisible.length > 0 && (
        <div className="wb-bulkbar" role="region" aria-label={t("workboard.bulk.title")} data-testid="wb-bulkbar">
          <span className="wb-bulk-count" data-testid="wb-bulk-count">
            {t("workboard.bulk.selected", { n: checkedVisible.length })}
          </span>
          <span style={{ flex: 1 }} />
          <Btn
            variant="secondary"
            size="sm"
            data-testid="wb-reassign"
            disabled={!can("tasks", "write") || !commonPerformer}
            title={!commonPerformer ? t("workboard.bulk.reassignNeedsOne") : undefined}
            onClick={() => {
              setToUserId("");
              setReassignOpen(true);
            }}
          >
            <Icon d={ICONS.users} size={15} />
            {t("workboard.bulk.reassign")}
          </Btn>
          <Btn
            variant="secondary"
            size="sm"
            data-testid="wb-reschedule"
            disabled={!can("tasks", "write")}
            onClick={() => {
              setDueDate("");
              setAvoidWeekend(false);
              setRescheduleOpen(true);
            }}
          >
            <Icon d={ICONS.calendar} size={15} />
            {t("workboard.bulk.reschedule")}
          </Btn>
          <Btn variant="ghost" size="sm" data-testid="wb-clear-sel" onClick={clearSelection}>
            {t("workboard.bulk.clear")}
          </Btn>
        </div>
      )}

      <div className="d-card">
        <div className="d-card-body" style={{ padding: 0 }}>
          <div className="wb-row wb-head" role="row">
            <span className="wb-check-cell">
              <input
                type="checkbox"
                aria-label={t("workboard.bulk.selectAll")}
                data-testid="wb-select-all"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked;
                }}
                onChange={toggleAll}
                disabled={rows.length === 0}
              />
            </span>
            {SORT_COLS.map(({ key, labelKey }) => (
              <button
                key={key}
                type="button"
                className={`wb-sort ${sortKey === key ? "on" : ""}`}
                aria-sort={ariaSort(key)}
                data-testid={`wb-sort-${key}`}
                onClick={() => toggleSort(key)}
              >
                {t(labelKey)}
                {sortKey === key && <SortCaret dir={sortDir} />}
              </button>
            ))}
            <span />
          </div>

          {activeQ.isLoading && (
            <div style={{ padding: "8px 12px" }}>
              <Skeleton variant="text" count={5} />
            </div>
          )}

          {!activeQ.isLoading && rows.length === 0 && (
            <EmptyState icon={ICONS.list} title={t("workboard.empty")} compact />
          )}

          {!activeQ.isLoading &&
            rows.map((task) => {
              const [chipKind, chipKey] = STATUS_CHIP[task.status] ?? (["paused", null] as const);
              const chipLabel = chipKey ? t(chipKey) : task.status;
              const intervalUnits = task.interval?.calendarDays ?? task.interval?.counterHours ?? 0;
              const late = task.dueDays && task.dueDays > 0 ? task.dueDays : 0;
              const ov = intervalUnits > 0 && late > 0 ? relativeOverdue(late, intervalUnits) : null;
              const ledBand = ov ? (ov.band === "alarm" ? "crit" : "warn") : "ok";
              const isChecked = checked.has(task.id);
              return (
                <div key={task.id} className={`wb-row ${selected?.id === task.id ? "sel" : ""} ${isChecked ? "checked" : ""}`} role="row">
                  <span className="wb-check-cell">
                    <input
                      type="checkbox"
                      aria-label={t("workboard.bulk.selectRow", { wo: task.number ?? task.title })}
                      data-testid={`wb-check-${task.id}`}
                      checked={isChecked}
                      onChange={() => toggleRow(task.id)}
                    />
                  </span>
                  <button className="wb-rowmain" onClick={() => setSelected(task)} data-testid={`wb-open-${task.id}`}>
                    <span className={`wb-led ${ledBand}`} aria-hidden="true" />
                    <span className="wo-id">{task.number ?? task.id.slice(0, 8)}</span>{" "}
                    <span className="wo-title">{task.title}</span>
                    {task.priority && <span className={`prio-chip ${PRIO_CHIP[task.priority]}`}>{task.priority}</span>}
                  </button>
                  <span>
                    <StatusChip kind={chipKind}>{chipLabel}</StatusChip>
                  </span>
                  <span>
                    {ov ? (
                      <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <OverdueBar late={late} interval={intervalUnits} />
                        <span style={{ color: "var(--status-warning-ink)", fontSize: 12.5 }}>+{late}</span>
                      </span>
                    ) : (
                      <span className="on-time">on schedule</span>
                    )}
                  </span>
                  <span>
                    <SyncBadge state={stateFor(`tasks:${task.id}`)} compact />
                  </span>
                </div>
              );
            })}
        </div>
      </div>

      {selected && (
        <WoDetail
          task={selected}
          onClose={() => setSelected(null)}
          userName={userName}
          deviceName={deviceName}
        />
      )}

      {reassignOpen && (
        <div className="modal-scrim" onClick={() => setReassignOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 480 }} role="dialog" aria-label={t("workboard.bulk.reassignTitle")}>
            <div className="modal-hd">
              <div className="ic" style={{ background: "var(--brand-wash)", color: "var(--brand-primary)" }}>
                <Icon d={ICONS.users} size={20} />
              </div>
              <h3>{t("workboard.bulk.reassignTitle")}</h3>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                {t("workboard.bulk.reassignFrom", { from: commonPerformer ? userName(commonPerformer) : "—" })}
              </p>
              <div className="note" style={{ marginBottom: 14 }}>
                <Icon d={ICONS.alert} size={16} />
                <span>{t("workboard.bulk.reassignScopeNote")}</span>
              </div>
              <div className="d-field">
                <label htmlFor="wb-to-user">{t("workboard.bulk.newPerformer")}</label>
                <div className="field-ctrl">
                  <select
                    id="wb-to-user"
                    data-testid="wb-reassign-to"
                    value={toUserId}
                    onChange={(e) => setToUserId(e.target.value)}
                  >
                    <option value="">{t("workboard.bulk.selectUser")}</option>
                    {users
                      .filter((u) => u.id !== commonPerformer)
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {String(u.name)}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <Btn variant="ghost" size="sm" onClick={() => setReassignOpen(false)}>
                {t("common.cancel")}
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                data-testid="wb-reassign-confirm"
                disabled={!toUserId || mass.isPending}
                onClick={() => void onReassign()}
              >
                {t("workboard.bulk.reassign")}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {rescheduleOpen && (
        <div className="modal-scrim" onClick={() => setRescheduleOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 480 }} role="dialog" aria-label={t("workboard.bulk.rescheduleTitle")}>
            <div className="modal-hd">
              <div className="ic" style={{ background: "var(--brand-wash)", color: "var(--brand-primary)" }}>
                <Icon d={ICONS.calendar} size={20} />
              </div>
              <h3>{t("workboard.bulk.rescheduleTitle")}</h3>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>{t("workboard.bulk.selected", { n: checkedVisible.length })}</p>
              <div className="d-field">
                <label htmlFor="wb-due-date">{t("workboard.bulk.rescheduleDate")}</label>
                <div className="field-ctrl">
                  <input
                    id="wb-due-date"
                    type="date"
                    data-testid="wb-reschedule-date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                <input
                  type="checkbox"
                  data-testid="wb-reschedule-avoid"
                  checked={avoidWeekend}
                  onChange={(e) => setAvoidWeekend(e.target.checked)}
                />
                <span>{t("workboard.bulk.rescheduleAvoidWeekend")}</span>
              </label>
            </div>
            <div className="modal-foot">
              <Btn variant="ghost" size="sm" onClick={() => setRescheduleOpen(false)}>
                {t("common.cancel")}
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                data-testid="wb-reschedule-confirm"
                disabled={!dueDate || reschedule.isPending}
                onClick={() => void onReschedule()}
              >
                {t("workboard.bulk.reschedule")}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Tiny up/down caret reflecting the active sort direction. */
function SortCaret({ dir }: { dir: SortDir }) {
  return (
    <Icon d={dir === "asc" ? ICONS.chevronUp : ICONS.chevronDown} size={13} sw={2.4} style={{ marginLeft: 4 }} />
  );
}
